import { useShallow } from 'zustand/react/shallow';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { RelationshipType as R, type Channel, type Relationship } from '@comms/shared';
import { useStore } from '../store';
import { api } from '../lib/api';
import { Avatar, Button, ErrorText, Icon, Input, Tabs } from '../components/ui';
import { NavButton } from '../components/MobileNav';

type Tab = 'online' | 'all' | 'pending' | 'blocked' | 'add';

export function FriendsView() {
  const rels = useStore(useShallow((s) => Object.values(s.relationships)));
  const presences = useStore((s) => s.presences);
  const [tab, setTab] = useState<Tab>('online');
  const [username, setUsername] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();

  const list = rels.filter((r) => {
    if (tab === 'online')
      return r.type === R.FRIEND && (presences[r.id]?.status ?? 'offline') !== 'offline';
    if (tab === 'all') return r.type === R.FRIEND;
    if (tab === 'pending') return r.type === R.INCOMING || r.type === R.OUTGOING;
    if (tab === 'blocked') return r.type === R.BLOCKED;
    return false;
  });

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      await api('POST', '/users/@me/relationships', { username: username.trim().toLowerCase() });
      setResult(`Friend request sent to ${username}.`);
      setUsername('');
    } catch (err) {
      setError(err);
    }
  }

  async function openDm(r: Relationship) {
    const c = await api<Channel>('POST', '/users/@me/channels', { recipientIds: [r.id] });
    navigate(`/channels/@me/${c.id}`);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-4 border-b border-bg-0 px-4">
        <NavButton />
        <span className="flex items-center gap-2 font-semibold text-text-0">
          <Icon name="users" /> Friends
        </span>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: 'online', label: 'Online' },
            { id: 'all', label: 'All' },
            { id: 'pending', label: 'Pending' },
            { id: 'blocked', label: 'Blocked' },
          ]}
        />
        <Button className="py-1" onClick={() => setTab('add')}>
          Add Friend
        </Button>
      </header>
      <div className="flex-1 overflow-y-auto p-6">
        {tab === 'add' ? (
          <form onSubmit={add} className="max-w-xl">
            <h2 className="font-bold text-text-0">ADD FRIEND</h2>
            <p className="mb-3 text-sm text-text-3">You can add friends by their exact username.</p>
            <div className="flex gap-2">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
              />
              <Button type="submit" disabled={!username.trim()}>
                Send request
              </Button>
            </div>
            {result && <p className="mt-2 text-sm text-success">{result}</p>}
            <ErrorText error={error} />
          </form>
        ) : (
          <>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-text-3">
              {tab} — {list.length}
            </h3>
            {list.length === 0 && <p className="text-text-3">Nobody here yet.</p>}
            {list.map((r) => (
              <div key={r.id} className="flex items-center gap-3 border-t border-bg-3 py-3">
                <Avatar userId={r.id} showStatus />
                <div className="flex-1">
                  <div className="font-semibold text-text-0">{r.user.displayName}</div>
                  <div className="text-sm text-text-3">
                    {r.type === R.INCOMING
                      ? 'Incoming friend request'
                      : r.type === R.OUTGOING
                        ? 'Outgoing friend request'
                        : (presences[r.id]?.customStatus ?? r.user.username)}
                  </div>
                </div>
                {r.type === R.FRIEND && (
                  <Button variant="secondary" className="py-1" onClick={() => void openDm(r)}>
                    Message
                  </Button>
                )}
                {r.type === R.INCOMING && (
                  <Button
                    className="py-1"
                    onClick={() => void api('PUT', `/users/@me/relationships/${r.id}`, {})}
                  >
                    Accept
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="py-1"
                  onClick={() => void api('DELETE', `/users/@me/relationships/${r.id}`)}
                >
                  {r.type === R.FRIEND
                    ? 'Remove'
                    : r.type === R.BLOCKED
                      ? 'Unblock'
                      : r.type === R.INCOMING
                        ? 'Ignore'
                        : 'Cancel'}
                </Button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
